/**
 * Canonical public entity route registry.
 *
 * This file owns the shared path shapes used across Kreate.love applications.
 * It is intentionally additive: current live canonical routes are centralized
 * without changing route behavior or legacy redirects.
 */

export type CanonicalPublicEntityType =
  | 'profile'
  | 'katalag'
  | 'sub_katalag'
  | 'katalag_deal'
  | 'sub_katalag_deal'
  | 'brand_store'
  | 'wishlist';

export interface CanonicalPublicRouteDefinition {
  entityType: CanonicalPublicEntityType;
  pattern: string;
  status: 'active' | 'reserved';
}

export interface LegacyPublicRouteDefinition {
  pattern: string;
  redirectsTo: 'brand_store';
}

/**
 * Active patterns reflect the MVP public routing contract.
 * Profile remains registered only as a reserved compatibility route.
 * Wishlist is active only at the single owner-scoped MVP path.
 */
export const CANONICAL_PUBLIC_ROUTE_REGISTRY = {
  profile: {
    entityType: 'profile',
    pattern: '/profile/:handle',
    status: 'reserved',
  },
  katalag: {
    entityType: 'katalag',
    pattern: '/:handle',
    status: 'active',
  },
  subKatalag: {
    entityType: 'sub_katalag',
    pattern: '/:handle/:catalogSlug',
    status: 'active',
  },
  katalagDeal: {
    entityType: 'katalag_deal',
    pattern: '/:handle/deals/:dealId',
    status: 'active',
  },
  subKatalagDeal: {
    entityType: 'sub_katalag_deal',
    pattern: '/:handle/:catalogSlug/deal/:dealId',
    status: 'active',
  },
  brandStore: {
    entityType: 'brand_store',
    pattern: '/brand/:handle',
    status: 'active',
  },
  brandChildStore: {
    entityType: 'brand_store',
    pattern: '/brand/:handle/:slug',
    status: 'active',
  },
  wishlist: {
    entityType: 'wishlist',
    pattern: '/wishlist/:ownerHandle',
    status: 'active',
  },
} as const satisfies Record<string, CanonicalPublicRouteDefinition>;

export const LEGACY_PUBLIC_ROUTE_REGISTRY = {
  storeBySlug: {
    pattern: '/store/:slug',
    redirectsTo: 'brand_store',
  },
  handleStore: {
    pattern: '/:handle/store',
    redirectsTo: 'brand_store',
  },
} as const satisfies Record<string, LegacyPublicRouteDefinition>;

const CANONICAL_HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

function canonicalSegment(
  value: string,
  label: string,
  minLength: number,
  maxLength: number,
): string {
  const normalized = value.trim().toLowerCase();
  if (
    !normalized ||
    normalized.length < minLength ||
    normalized.length > maxLength ||
    !CANONICAL_HANDLE_PATTERN.test(normalized) ||
    normalized.includes('--')
  ) {
    throw new Error(`${label} must be a canonical handle segment`);
  }
  return encodeURIComponent(normalized);
}

const CANONICAL_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function canonicalUuidSegment(value: string, label: string): string {
  const normalized = value.trim().toLowerCase();
  if (!CANONICAL_UUID_PATTERN.test(normalized)) {
    throw new Error(`${label} must be a canonical UUID`);
  }
  return normalized;
}

function canonicalOrigin(origin: string): string {
  let parsedOrigin: URL;
  try {
    parsedOrigin = new URL(origin.trim());
  } catch {
    throw new Error('origin must be an absolute http(s) origin');
  }

  if (
    !['http:', 'https:'].includes(parsedOrigin.protocol) ||
    parsedOrigin.username ||
    parsedOrigin.password ||
    parsedOrigin.pathname !== '/' ||
    parsedOrigin.search ||
    parsedOrigin.hash
  ) {
    throw new Error('origin must contain only scheme, host, and optional port');
  }

  return parsedOrigin.origin;
}

export function buildProfilePath(profileHandle: string): string {
  return `/profile/${canonicalSegment(profileHandle, 'profileHandle', 1, 80)}`;
}

/**
 * Main Katalag: /<katalagHandle>
 * Sub-Katalag:  /<katalagHandle>/<subHandle>
 */
export function buildKatalagPath(katalagHandle: string, subHandle?: string | null): string {
  const main = canonicalSegment(katalagHandle, 'katalagHandle', 3, 30);
  const child = subHandle?.trim() ? canonicalSegment(subHandle, 'subHandle', 1, 48) : null;
  return child ? `/${main}/${child}` : `/${main}`;
}

export function buildKatalagDealPath(
  katalagHandle: string,
  dealId: string,
  subHandle?: string | null,
): string {
  const main = canonicalSegment(katalagHandle, 'katalagHandle', 3, 30);
  const deal = canonicalUuidSegment(dealId, 'dealId');
  const child = subHandle?.trim() ? canonicalSegment(subHandle, 'subHandle', 1, 48) : null;
  return child ? `/${main}/${child}/deal/${deal}` : `/${main}/deals/${deal}`;
}

export function buildBrandStorePath(brandHandle: string, storeHandle?: string | null): string {
  const brand = canonicalSegment(brandHandle, 'brandHandle', 1, 80);
  const child = storeHandle?.trim() ? canonicalSegment(storeHandle, 'storeHandle', 1, 80) : null;
  return child ? `/brand/${brand}/${child}` : `/brand/${brand}`;
}

export function buildWishlistPath(ownerHandle: string, wishlistHandle?: string | null): string {
  const owner = canonicalSegment(ownerHandle, 'ownerHandle', 1, 80);

  if (wishlistHandle?.trim()) {
    throw new Error('nested Wishlist paths are not part of the MVP public contract');
  }

  return `/wishlist/${owner}`;
}

export function buildLegacyStorePath(brandHandle: string): string {
  return `/store/${canonicalSegment(brandHandle, 'brandHandle', 1, 80)}`;
}

export function buildLegacyHandleStorePath(brandHandle: string): string {
  return `/${canonicalSegment(brandHandle, 'brandHandle', 1, 80)}/store`;
}

export function buildPublicSurfaceUrl(origin: string, path: string): string {
  if (!path.startsWith('/') || path.startsWith('//')) {
    throw new Error('path must be an absolute application path');
  }
  return `${canonicalOrigin(origin)}${path}`;
}

export function buildProfileSurfaceUrl(origin: string, profileHandle: string): string {
  return buildPublicSurfaceUrl(origin, buildProfilePath(profileHandle));
}

export function buildKatalagSurfaceUrl(
  origin: string,
  katalagHandle: string,
  subHandle?: string | null,
): string {
  return buildPublicSurfaceUrl(origin, buildKatalagPath(katalagHandle, subHandle));
}

export function buildKatalagDealSurfaceUrl(
  origin: string,
  katalagHandle: string,
  dealId: string,
  subHandle?: string | null,
): string {
  return buildPublicSurfaceUrl(origin, buildKatalagDealPath(katalagHandle, dealId, subHandle));
}

export function buildBrandStoreSurfaceUrl(
  origin: string,
  brandHandle: string,
  storeHandle?: string | null,
): string {
  return buildPublicSurfaceUrl(origin, buildBrandStorePath(brandHandle, storeHandle));
}

export function buildWishlistSurfaceUrl(
  origin: string,
  ownerHandle: string,
  wishlistHandle?: string | null,
): string {
  return buildPublicSurfaceUrl(origin, buildWishlistPath(ownerHandle, wishlistHandle));
}
